import { useMemo } from "react";
import { useQuery, type QueryKey } from "react-query";

import type { ListStartedFeaturesResponse } from "@/api/splashpages";
import type { Feature } from "@/types/feature";
import { getCurrentUser } from "@/auth/firebase";
import { makeRequest } from "@/util";

export type UseHasEngagedWithFeatureResult = {
  engaged: boolean;
  loading: boolean;
};

export function startedFeaturesQueryKey(userId: string | undefined): QueryKey {
  return ["list_started_features", { userId: userId ?? "__anonymous__" }];
}

export function useHasEngagedWithFeature(
  feature: Feature,
): UseHasEngagedWithFeatureResult {
  // Use the Firebase UID directly so this works even when masquerading,
  // where currentUser in the Redux store is undefined (the employee's UID
  // isn't in the customer's team list).
  const userId = getCurrentUser()?.uid;

  const { data, isLoading } = useQuery<ListStartedFeaturesResponse>(
    startedFeaturesQueryKey(userId),
    async () => {
      const res = await makeRequest<ListStartedFeaturesResponse>(
        "list_started_features",
        {},
      );
      return res.data;
    },
    {
      enabled: !!userId,
      staleTime: 60_000,
    },
  );

  const startedSet = useMemo(
    () => new Set((data?.features ?? []).map((f) => f.featureName)),
    [data],
  );

  // Pending userId is also loading; otherwise consumers would see a
  // "not engaged" flash before the query starts.
  return {
    engaged: startedSet.has(feature),
    loading: !userId || isLoading,
  };
}
